<template>
  <div
    class="stage-explorer"
    :class="{ collapsed, peeking: collapsed && peeking }"
    :style="(!collapsed || peeking) ? { width: width + 'px' } : null"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="onPanelClick"
  >
    <div v-if="!collapsed" class="se-resize" title="Drag to resize" @mousedown="startResize"></div>
    <div class="se-head">
      <span v-if="!collapsed || peeking" class="se-title">{{ selectedStage ? 'Stage Details' : 'Stage Explorer' }}</span>
      <button class="se-toggle" :title="collapsed ? 'Expand' : 'Collapse'" @click="collapsed = !collapsed">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
    </div>

    <div v-if="!collapsed || peeking" class="se-body">
      <!-- Detail view for the currently selected stage -->
      <div v-if="selectedStage">
        <button class="se-back" @click="$emit('deselect')">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back to list
        </button>
        <div class="se-detail-name">{{ selectedStage.name }}</div>
        <span class="se-badge" :class="'se-badge-' + selectedStage.arche">{{ selectedStage.emoji }} {{ selectedStage.label }}</span>

        <div class="se-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="se-tab"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >{{ tab.key === 'comments' ? `Comments (${commentCount})` : tab.label }}</button>
        </div>

        <stage-content-tab
          v-if="activeTab === 'content'"
          :stage="selectedStage"
          :lang-options="langOptions"
          :current-lang="currentLang"
          :all-stages="stages"
          @select-stage="$emit('select-stage', $event)"
          @update-message="$emit('update-message', $event)"
          @update-topics="$emit('update-topics', $event)"
        />
        <stage-behavior-tab v-else-if="activeTab === 'behavior'" />
        <stage-comments-tab
          v-else-if="activeTab === 'comments'"
          :journey-key="journeyKey"
          :stage-name="selectedStage.name"
        />
      </div>

      <!-- Stage list -->
      <div v-else>
        <div class="se-legend">
          <div v-for="item in legend" :key="item.key" class="se-legend-item">
            <span class="se-swatch" :style="{ background: item.bg, borderColor: item.border }"></span>
            <span>{{ item.label }}</span>
          </div>
        </div>

        <div class="se-stages-head">Stages · {{ stages.length }}</div>
        <input v-model="filterText" class="se-filter" type="text" placeholder="Filter stages..." />

        <div class="se-list">
          <div v-for="s in filteredStages" :key="s.name" class="se-card" @click="$emit('select-stage', s.name)">
            <div class="se-name">{{ s.name }}</div>
            <div class="se-meta">
              <span class="se-badge" :class="'se-badge-' + s.arche">{{ s.emoji }} {{ s.label }}</span>
              <span class="se-desc">{{ s.desc }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StageContentTab from './StageContentTab.vue'
import StageBehaviorTab from './StageBehaviorTab.vue'
import StageCommentsTab from './StageCommentsTab.vue'
import { listComments } from './journeyComments'

const TABS = [
  { key: 'content', label: 'Content' },
  { key: 'behavior', label: 'Behavior' },
  { key: 'comments', label: 'Comments' }
]

export default {
  name: 'stage-explorer',
  components: { StageContentTab, StageBehaviorTab, StageCommentsTab },
  props: {
    stages: { type: Array, default: () => [] },
    selectedStage: { type: Object, default: null },
    langOptions: { type: Array, default: () => [] },
    currentLang: { type: String, default: '' },
    journeyKey: { type: String, default: '' }
  },
  data () {
    return {
      collapsed: true,
      autoOpened: false,
      justOpened: false,
      peeking: false,
      resizing: false,
      width: 380,
      filterText: '',
      tabs: TABS,
      activeTab: 'content',
      legend: [
        { key: 'info', label: 'Message', bg: 'rgba(59,130,246,0.12)', border: '#3b82f6' },
        { key: 'choice', label: 'Question', bg: 'rgba(22,163,74,0.12)', border: '#16a34a' },
        { key: 'text', label: 'Text input', bg: 'rgba(234,88,12,0.12)', border: '#ea580c' },
        { key: 'pf', label: 'Auto-route', bg: 'rgba(124,58,237,0.12)', border: '#7c3aed' },
        { key: 'end', label: 'End', bg: '#e5e7eb', border: '#9aa0ab' }
      ]
    }
  },
  computed: {
    filteredStages () {
      const q = this.filterText.trim().toLowerCase()
      if (!q) return this.stages
      return this.stages.filter(s => s.name.toLowerCase().includes(q))
    },
    isOpen () {
      return !this.collapsed || this.peeking
    },
    effectiveWidth () {
      return this.isOpen ? this.width : 40
    },
    commentCount () {
      return this.selectedStage ? listComments(this.journeyKey, this.selectedStage.name).length : 0
    }
  },
  watch: {
    selectedStage (val, oldVal) {
      if (val) {
        // A stage can be selected by clicking something outside this panel (a canvas
        // card, the right-click "Add Comment" menu item) — that same click's document
        // listener runs the outside-click-collapse check a moment later (Vue may flush
        // this watcher synchronously mid-dispatch). Guard the panel open it just did.
        if (this.collapsed) { this.collapsed = false; this.autoOpened = true; this.justOpened = true }
        if (!oldVal || oldVal.name !== val.name) this.activeTab = 'content'
      } else if (this.autoOpened) {
        this.collapsed = true
        this.autoOpened = false
      }
    },
    effectiveWidth (val) {
      this.$emit('width-change', val)
    }
  },
  mounted () {
    this.onDocClick = e => {
      if (this.justOpened) { this.justOpened = false; return }
      if (!this.resizing && !this.collapsed && !this.$el.contains(e.target)) this.collapsed = true
    }
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onMouseEnter () {
      if (this.collapsed) this.peeking = true
    },
    onMouseLeave () {
      this.peeking = false
    },
    onPanelClick () {
      if (this.collapsed && this.peeking) {
        this.collapsed = false
        this.peeking = false
      }
    },
    setCollapsed (v) {
      this.collapsed = v
    },
    startResize (e) {
      const startX = e.clientX
      const startW = this.width
      this.resizing = true
      const onMove = (ev) => {
        let newW = startW - (ev.clientX - startX)
        newW = Math.max(260, Math.min(480, newW))
        this.width = newW
      }
      const onUp = () => {
        this.resizing = false
        document.body.classList.remove('resizing-stage-explorer')
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      document.body.classList.add('resizing-stage-explorer')
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
      e.preventDefault()
    }
  }
}
</script>

<style>
body.resizing-stage-explorer{cursor:col-resize;user-select:none}
</style>

<style scoped>
.stage-explorer{
  --card:#ffffff;--card-border:#E2E8EE;--card-label-bg:#F6F8FA;
  --text:#16233A;--text-muted:#5D6C7E;--text-faint:#94A2B1;
  --accent:#0D9488;--accent-light:#0B7A70;--accent-strong:#0B7A70;
  --btn-hl-border:rgba(13,148,136,0.4);
  position:fixed;top:0;right:0;bottom:0;
  background:var(--card);border-left:1px solid var(--card-border);
  box-shadow:-4px 0 16px rgba(0,0,0,0.06);
  display:flex;flex-direction:column;overflow:hidden;z-index:900;
  transition:width .15s ease,box-shadow .15s ease;
}
.stage-explorer.collapsed{width:40px}
.stage-explorer.peeking{box-shadow:-8px 0 28px rgba(0,0,0,0.16)}
.se-resize{position:absolute;top:0;left:0;width:6px;height:100%;cursor:col-resize;z-index:2}
.se-head{
  padding:10px 12px;border-bottom:1px solid var(--card-border);
  display:flex;align-items:center;gap:8px;flex-shrink:0;
}
.stage-explorer.collapsed:not(.peeking) .se-head{justify-content:center;padding:10px 4px}
.se-title{font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;flex:1}
.se-toggle{
  width:24px;height:24px;border-radius:6px;background:transparent;border:1px solid transparent;
  color:var(--text-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.se-toggle:hover{background:var(--card-label-bg);color:var(--text);border-color:var(--card-border)}
.se-toggle svg{transition:transform .15s}
.stage-explorer.collapsed .se-toggle svg{transform:rotate(180deg)}

.se-body{flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column}
.se-body::-webkit-scrollbar{width:6px}
.se-body::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}

.se-legend{margin-bottom:14px}
.se-legend-item{display:flex;align-items:center;gap:8px;padding:3px 0;font-size:12px;color:var(--text)}
.se-swatch{width:12px;height:12px;border-radius:3px;border:1px solid;flex-shrink:0}

.se-stages-head{font-size:10.5px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px}
.se-filter{
  width:100%;padding:6px 10px;border:1px solid var(--card-border);border-radius:6px;
  font-size:12px;background:var(--card);color:var(--text);margin-bottom:10px;
}
.se-filter::placeholder{color:var(--text-faint)}
.se-filter:focus{outline:none;border-color:var(--accent)}

.se-list{display:flex;flex-direction:column;gap:5px}
.se-card{
  padding:9px 10px;border:1px solid var(--card-border);border-radius:8px;
  background:var(--card);cursor:pointer;transition:all .1s;
}
.se-card:hover{border-color:var(--btn-hl-border);background:var(--card-label-bg)}
.se-name{font-family:'JetBrains Mono',monospace;font-size:11.5px;font-weight:600;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.se-meta{font-size:10.5px;color:var(--text-muted);margin-top:4px;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.se-badge{display:inline-flex;align-items:center;gap:3px;font-size:9.5px;font-weight:600;padding:1px 7px;border-radius:999px}
.se-badge-info{background:rgba(59,130,246,0.12);color:#1d4ed8}
.se-badge-choice{background:rgba(22,163,74,0.12);color:#15803d}
.se-badge-text{background:rgba(234,88,12,0.12);color:#c2410c}
.se-badge-pf{background:rgba(124,58,237,0.12);color:#7c3aed}
.se-badge-end{background:#e5e7eb;color:#4b5563}

.se-back{
  display:inline-flex;align-items:center;gap:5px;background:none;border:none;
  color:var(--accent-light);cursor:pointer;font-size:12px;font-weight:500;padding:0;margin-bottom:12px;
}
.se-back:hover{text-decoration:underline}
.se-detail-name{font-family:'JetBrains Mono',monospace;font-size:13px;font-weight:600;color:var(--text);margin-bottom:6px;word-break:break-all}

.se-tabs{display:flex;gap:16px;border-bottom:1px solid var(--card-border);margin:14px 0 16px}
.se-tab{
  background:none;border:none;padding:0 0 9px;font-family:inherit;font-size:12.5px;font-weight:600;
  color:var(--text-faint);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;
}
.se-tab:hover{color:var(--text)}
.se-tab.active{color:var(--accent-strong);border-bottom-color:var(--accent-strong)}
</style>



// WEBPACK FOOTER //
// StageExplorer.vue?21c5ab56